// Utilities extracted from Services.js

import _ from "lodash";
import axiosLib from "axios";
import axios from "lib/axiosInstance";
import update from "immutability-helper";
import { ATTACHMENT_CREATE_ATTRIBUTES } from "store/static";
import client from "lib/apolloClient/client";
import i18next from "i18next";
import {
  formatBytes,
  getCountString,
  getYearDurationFormat,
  getFileType,
  isTypingInInput,
} from "Utils";
import { NOTIFICATION_CATEGORY_V2 } from "Constants";
import {
  checkForSimilarityMutation,
  cancelCheckSimilarityMutation,
  updateAIConversationMutation,
} from "modules/CommonMutations";
import {
  getAttachmentQuery,
  getMultiAttachmentsQuery,
} from "modules/CommonQuery";
import {
  getSubjectBasicDetailsFromCache,
  getCriteriaSetSlimDetailsFromCache,
  getOrganizationDetailsFromCache,
  getPlatformUserDetailsFromCache,
  getAttachmentFragmentFromCache,
  writeAttachmentSimilarityReportInCache,
  getGlobalConstantsFromCache,
} from "modules/CommonGraphqlHelpers";
import ACLStore from "lib/aclStore";
import queryString from "query-string";
import {
  ELEMENT_TYPE_UBD_LEARNING_STANDARD,
  ELEMENT_TYPE_UBD_SUBJECT,
} from "Constants/stringConstants";
import {
  ENABLE_DYNAMIC_LOCALISATION_OF_TEMPLATE,
  ENABLE_FAMILY_INVITE_FLOW_V2,
  ENABLE_FAMILY_INVITE_PDF_V2,
} from "Constants/permissionConstants";
import { getIsAdminByRole } from "lib/staffPermissionUtils";
import { enableFamilyInviteV2Flow, getIsChina } from "config";
import {
  ASSESSMENT_TOOLS,
  parseStreamResponse as parseStreamResponseV2,
} from "@toddle-edu/common-utils";
import { DESIGNATIONS_CONSTANTS } from "./ServicesConstants";
import { getEnvironment } from "Utils/UrlHelpers";
import { convertMarkdownToHTML } from "Utils/AIUtils";

const PATH_TO_GET_SIGNED_URL = `/auth/generateS3SignedURL`;

const CancelToken = axiosLib.CancelToken;

const QUERY_STRING_CONFIG = { parseBooleans: true };

const XLSX_FILE_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";

const SHEET_NAME_MAX_LENTH = 31;

// `STANDARD_UIDS` moved to:
// src/modules/Services/ServicesConstants.js

export const getPlannerElementSetDynamicLabelsMemoize = _.memoize(
  params => getPlannerElementSetDynamicLabels(params),
  ({ plannerElementSets }) =>
    JSON.stringify(
      _.map(plannerElementSets, set => _.pick(set, ["type", "label"]))
    )
);

export const getPlannerElementSetDynamicLabels = ({ plannerElementSets }) => {
  const plannerElementSetLabels = _.reduce(
    plannerElementSets,
    (result, { type, label }) => {
      result[type] = label;
      return result;
    },
    {}
  );
  return plannerElementSetLabels;
};

// `getFieldContextFromParentType` moved to:
// src/routes/routes/Teacher/routes/Courses/routes/Course/routes/UnitPlans/modules/Utils.js

// `allowLearningGoalsRatingInStandards`, `allowLearningGoalsRatingInSkills`,
// and `getPlannerElementsToEvaluate` moved to:
// src/routes/routes/Teacher/routes/Courses/routes/Course/routes/ClassRoom/modules/utils.js

/**
 * Returns total linked identities based on identity flow
 *
 * @param {*} {id: string, type:string}
 * @returns {Int} Total linked identities
 */
export const getTotalLinkedIdentities = ({ id, type }) => {
  const { linkedIdentityUsersV2 } = getPlatformUserDetailsFromCache({
    id,
    type,
  });

  return _.get(linkedIdentityUsersV2, "totalCount", 0);
};

export const getAcademicYearById = ({ organizationId, academicYearId }) => {
  const organizationDetails = getOrganizationDetailsFromCache(organizationId);
  const academicYears = _.get(organizationDetails, "academicYears", []);
  return _.find(academicYears, { id: academicYearId });
};

//This Function returns the start and endDate of any entity when academicYears are passed as an input
//In case of multiple academicYears we need startDate of the first academicYear
//and endDate of the last academicYear, This function is used in that case.

export const getStartEndDatesOfAcademicYears = ({ academicYears }) => {
  const sortedAcademicYears = _.sortBy(academicYears, [item => item.startDate]);
  return {
    startDate: _.get(_.first(sortedAcademicYears), "startDate", null),
    endDate: _.get(_.last(sortedAcademicYears), "endDate", null),
  };
};

// `getCourseAcademicYearDates` and `getCourseAcademicYearDatesMemoize` moved to:
// src/routes/routes/Teacher/routes/Courses/routes/Course/modules/Utils.js

export const getAcademicYearsList = organizationId => {
  const organizationDetails = getOrganizationDetailsFromCache(organizationId);
  return _.map(organizationDetails.academicYears, academicYear => {
    const {
      id,
      startDate,
      endDate,
      isCurrentAcademicYear,
      isUpcomingAcademicYear,
    } = academicYear;
    return {
      startDate,
      endDate,
      isCurrentAcademicYear,
      isUpcomingAcademicYear,
      label: getYearDurationFormat({ startDate, endDate }),
      value: id,
    };
  });
};

export const getSubjectLabel = value => {
  if (value && !Array.isArray(value)) {
    return _.get(getSubjectBasicDetailsFromCache(value), "name", "");
  } else {
    return _.filter(
      _.map(value, item => {
        return _.get(getSubjectBasicDetailsFromCache(item), "name", "");
      }),
      val => !!val
    );
  }
};

export const getSubjectLabelAndIsArchived = value => {
  const subjectList = _.filter(
    _.map(_.castArray(value), item => {
      const subjectDetail = getSubjectBasicDetailsFromCache(item);
      return {
        name: _.get(subjectDetail, "name", ""),
        isArchived: _.get(subjectDetail, "isArchived", false),
      };
    }),
    val => !!val
  );
  return Array.isArray(value) ? subjectList : _.first(subjectList);
};

// The following functions/helpers moved to:
// src/modules/ElementValueUtils.js
//
// Exported:
//   getPypElementLabel, getPlannerElementLabel, getElementValue,
//   plannerElementValue, checkEmptyElementValue, getResolvedOptimisticValue,
//   getBenchMarkValueMemoized, getElementData
//
// Private helpers (also moved):
//   getFocusAreaLabel, getViewModeValueForMultiWindowTable,
//   getMultiFieldValue, checkIfWorldStudiesSubjectSelected, getOptionLabels,
//   getValueForListWithTags, getCheckListWithPopupEmptyStatus,
//   getCheckListWithPopupEmptyStatusMemoized, getMultiWindowTableCompletionStatus,
//   getFieldValuesOfMultiWindowTable, getFieldValuesOfMultiWindowTableMemoized,
//   getNotPypInputListLabel, getBenchMarkValue, getValueFromLabelValuePair, getLists

// `getUnitPlanCardThemeTextArr` moved to:
// src/routes/routes/Teacher/routes/Courses/routes/Course/routes/UnitPlans/modules/Utils.js

const _getIsGlobalSearchJumpToRevampEnabled = () => {
  return ACLStore.can("FeatureFlag:EnableGlobalSearchJumpToRevamp");
};

const _getIsGlobalSearchJumpToV2Enabled = () => {
  return ACLStore.can("FeatureFlag:EnableGlobalSearchJumpToV2");
};

const _shouldTriggerGlobalSearch = e => {
  const key = _.toLower(e.key);
  const isGlobalSearchJumpToRevampEnabled = _getIsGlobalSearchJumpToRevampEnabled();
  const isJumpToV2Enabled = _getIsGlobalSearchJumpToV2Enabled();

  return isGlobalSearchJumpToRevampEnabled && !isJumpToV2Enabled
    ? key === "j" && !isTypingInInput()
    : (e.ctrlKey || e.metaKey) && e.key === "/";
};

// `pypElementMapping`, `pypElementSubjectStandardsMapping`,
// `plannerElementFieldUidMap`, `pypElementLabelMapping`,
// and `pypElementSetMapping` moved to:
// src/Constants/plannerElementMappings.js

export const commentParentTypeToEntityMapping = parentType => {
  switch (parentType) {
    case "FIELD":
      return "PLANNER_FIELD";
    case "FLOW_SECTION":
      return "PLANNER_FLOW_SECTION";
    default:
      return parentType;
  }
};

export const getAttachment = async params => {
  const result = await client.query({
    query: getAttachmentQuery,
    variables: {
      ...params,
    },
    fetchPolicy: "network-only",
  });
  return result ? _.get(result, "data.node", {}) : {};
};

export const getMultiAttachments = async params => {
  const result = await client.query({
    query: getMultiAttachmentsQuery,
    variables: {
      ...params,
    },
    fetchPolicy: "network-only",
  });
  return result ? _.get(result, "data.node", {}) : {};
};

const getExceededSizeToastMsgAndLocaleParams = ({ fileTypesAndMaxSizes }) => {
  const getFileTypeLocalesAndText = mimetype => {
    const fileType = getFileType(mimetype);

    if (fileType.text == "FILE") {
      if (mimetype === "application/zip") {
        return { text: "ZIP", locale: "common:zip" };
      } else {
        return { text: "DOCUMENT", locale: "common:document" };
      }
    } else {
      return fileType;
    }
  };

  const getEntity1AndEntity2Value = ({ string }) => {
    return i18next.t("common:entity1_and_entity2", {
      entity1: _.join(_.slice(string, 0, -1), ", "),
      entity2: _.first(_.slice(string, -1)),
    });
  };

  if (_.size(_.uniqBy(fileTypesAndMaxSizes, "fileMaxSize")) === 1) {
    const sizeString = formatBytes({
      bytes: _.first(fileTypesAndMaxSizes).fileMaxSize,
    });
    const fileTypeString = getFileTypeLocalesAndText(
      _.first(fileTypesAndMaxSizes).fileType
    );

    return {
      msg: `toastMsgs:files_failed_with_same_file_type_And_file_size_msg`,
      locale_params: [
        {
          key: "count",
          value: _.size(fileTypesAndMaxSizes),
          isPlainText: true,
        },
        { key: "fileType", value: fileTypeString.locale, isPlainText: false },
        { key: "fileSize", value: sizeString, isPlainText: true },
      ],
    };
  } else {
    const uniqFileTypesAndMaxSizes = _.sortBy(
      _.uniqBy(fileTypesAndMaxSizes, "fileMaxSize"),
      "fileMaxSize"
    );

    const sizeString = _.map(uniqFileTypesAndMaxSizes, item =>
      formatBytes({
        bytes: item.fileMaxSize,
      })
    );

    const fileTypeString = _.map(uniqFileTypesAndMaxSizes, item =>
      i18next.t("common:lowercase", {
        text: i18next.t(getFileTypeLocalesAndText(item.fileType)?.locale),
      })
    );

    const sizeStringValue = getEntity1AndEntity2Value({ string: sizeString });

    const fileTypeStringValue = getEntity1AndEntity2Value({
      string: fileTypeString,
    });

    return {
      msg: `toastMsgs:files_failed_with_multiple_file_type_And_file_size_msg`,
      locale_params: [
        {
          key: "count",
          value: _.size(fileTypesAndMaxSizes),
          isPlainText: true,
        },
        { key: "fileTypes", value: fileTypeStringValue, isPlainText: true },
        { key: "fileSizes", value: sizeStringValue, isPlainText: true },
      ],
    };
  }
};

export const checkCriteriaSetOfPypTypeInLevelsLocalMemoize = _.memoize(
  params => checkCriteriaSetOfPypTypeInLevelsLocal(params),
  params => JSON.stringify(params)
);

export const checkCriteriaSetOfPypTypeInLevelsLocal = ({
  type,
  parentId = null,
  levelId,
  setId,
  ratingLevels,
  checkLevelId = true,
}) => {
  const filteredRatings = _.filter(ratingLevels, level => {
    const validateLevelId = checkLevelId ? level.levelId == levelId : true;

    if (parentId) {
      return (
        level.ibPYPElementType == type &&
        level.ibPYPParentElementId == parentId &&
        validateLevelId
      );
    } else {
      return level.ibPYPElementType == type && validateLevelId;
    }
  });

  const criteriaSets = _.map(
    _.reduce(
      filteredRatings,
      (result, rating) => {
        result = _.unionBy(result, rating.academicCriteriaSets, "id");
        return result;
      },
      []
    ),
    set => getCriteriaSetSlimDetailsFromCache(set.id)
  );

  return _.findIndex(criteriaSets, { id: setId }) >= 0;
};

export const filteredCriteriaSets = ({
  type,
  levelId,
  rootNodeId,
  node,
  getCriteriaSetOfPypType,
  criteriaType,
}) => {
  let filteredCriteriaSet = getCriteriaSetOfPypType({
    type,
    levelId,
    parentId: rootNodeId,
  });
  if (!_.isEmpty(node.children)) {
    let childFilteredCriteriaSet = [];
    _.forEach(node.children, childNode => {
      childFilteredCriteriaSet = _.unionBy(
        childFilteredCriteriaSet,
        filteredCriteriaSets({
          type: childNode.ibPlannerElementType,
          levelId: childNode.levelId,
          rootNodeId,
          node: childNode,
          getCriteriaSetOfPypType,
        }),
        "id"
      );
    });
    filteredCriteriaSet = _.unionBy(
      filteredCriteriaSet,
      childFilteredCriteriaSet,
      "id"
    );
  }

  if (criteriaType) {
    filteredCriteriaSet = _.filter(
      filteredCriteriaSet,
      set => set?.criteriaType == criteriaType
    );
  }

  return filteredCriteriaSet;
};

export const filteredCriteriaSetsMemoized = _.memoize(
  params => filteredCriteriaSets(params),
  params => JSON.stringify(params)
);

export const getGradeScalesByRootNodeIdMemoized = _.memoize(
  (standards, node) => {
    if (_.isEmpty(standards) || _.isEmpty(node)) {
      return [];
    }

    const nodeId = _.get(node, "id");

    const matchingStandardSet = _.find(standards, standardData =>
      _.isEqual(nodeId, _.get(standardData, "standardSet.id"))
    );

    if (!matchingStandardSet) {
      return [];
    }

    const subjectId = _.get(matchingStandardSet, "standardSet.subject.id");
    const plannerElementNodeSetId = _.get(
      matchingStandardSet,
      "standardSet.id"
    );

    // Process grade scales to include subject ID and plannerElementNodeSetId
    const gradeScales = _.map(
      _.get(matchingStandardSet, "standardSet.gradeScales", []),
      gradeScale => ({
        ...gradeScale,
        subjectId,
        plannerElementNodeSetId,
      })
    );

    return gradeScales;
  },
  // Memoize key generator - based on the standard set IDs and node IDs to ensure proper caching
  (standards, node) => {
    const standardSetIds = _.map(standards || [], "standardSet.id");
    const nodeId = _.get(node, "id");
    return JSON.stringify({ standardSetIds, nodeId });
  }
);

export const getCriteriaSetBasedOnPlannerElementType = ({
  type,
  parentId,
  getCriteriaSetOfPypType,
  criteriaType,
  preserveCriteriaSetOrder,
}) => {
  switch (type) {
    case ELEMENT_TYPE_UBD_LEARNING_STANDARD: {
      return getCriteriaSetOfPypType({
        type: [ELEMENT_TYPE_UBD_LEARNING_STANDARD, ELEMENT_TYPE_UBD_SUBJECT],
        parentId: parentId,
        criteriaType,
        preserveCriteriaSetOrder,
      });
    }
    default:
      return getCriteriaSetOfPypType({
        type,
        parentId: parentId,
        criteriaType,
        preserveCriteriaSetOrder,
      });
  }
};

export const isExcludedParentTypesForAttachmentCache = ({ parentType }) => {
  return _.includes(
    [
      "PROJECT_REPORT_SUBMISSION",
      "PROJECT_REPORT_SUBMISSION_FINAL",
      "ASSESSMENT_VOICE_INSTRUCTION",
      "ASSIGNMENT_RESOURCE_VOICE_INSTRUCTION",
      "PLANNER_FIELD_VIDEO_ATTACHMENT",
      "USER_SIGNATURE",
    ],
    parentType
  );
};

/**
 * @param {*} param0
 * @param {string} destinationSearch: Destination location search string
 * @param {array} queryParamsToPreserve: An array of query params to preserve on
 * switching route
 *
 * Note: For now, the query params "source_type", "preserve_query_params", "config" are being
 * preserved to support all possible edge cases.
 *
 * window.location.search = "?param1=value1"
 * destinationSearch = "?param2=value2" or "param2=value2"
 *
 * @returns {string} Query params like '?param1=value1&param2=value2' or ""
 */

export const getCombinedLocationSearch = ({
  destinationSearch = "",
  queryParamsToPreserve = ["source_type", "preserve_query_params", "config"],
}) => {
  let combinedQueryParams = {};

  const currentQueryParams = queryString.parse(
    window.location.search,
    QUERY_STRING_CONFIG
  );
  const destinationQueryParams = queryString.parse(
    destinationSearch,
    QUERY_STRING_CONFIG
  );

  const {
    preserve_query_params: preserveQueryParams = false,
  } = currentQueryParams;

  if (preserveQueryParams) {
    combinedQueryParams = {
      ..._.pick(currentQueryParams, queryParamsToPreserve),
      ...destinationQueryParams,
    };
  } else {
    combinedQueryParams = destinationQueryParams;
  }

  if (_.isEmpty(combinedQueryParams)) {
    return "";
  } else {
    return "?" + queryString.stringify(combinedQueryParams);
  }
};

const getMutationVariable = ({ files, isVirusTaggingEnabled, portalType }) => {
  return _.map(files, file => {
    return {
      fileName: _.get(file, "name", ""),
      fileMimeType: file.mimeType ? file.mimeType : "application/octet-stream",
      fileGroup: file?.metadata?.fileGroup ?? "content",
      fileExtension: _.get(file, ["metadata", "fileExtension"], ""),
      portalType,
      doVirusScanning: isVirusTaggingEnabled,
      id: file.resourceMapId,
    };
  });
};

const getIsVirusTaggingEnabled = () => {
  return ACLStore.can("Common:VirusScanTagging");
};

const uint8ArrayUtf8ByteString = (array, start, end) => {
  return String.fromCharCode(...array.slice(start, end));
};

// brands explained: https://github.com/strukturag/libheif/issues/83
// code adapted from: https://github.com/sindresorhus/file-type/blob/6f901bd82b849a85ca4ddba9c9a4baacece63d31/core.js#L428-L438
const isBufferHeic = buffer => {
  const brandMajor = uint8ArrayUtf8ByteString(buffer, 8, 12)
    .replace("\0", " ")
    .trim();

  return _.includes(
    ["mif1", "msf1", "heic", "heix", "hevc", "hevx"],
    brandMajor
  );
};

export const getDesignationsLocales = ({ t, isCheckForAdmin = true }) => {
  const globalConstants = getGlobalConstantsFromCache();
  const designations = _.get(globalConstants, "designations", []);
  const isAdmin = getIsAdminByRole();
  let filteredDesignations = [...designations];

  if (isCheckForAdmin && !isAdmin) {
    filteredDesignations = _.filter(
      filteredDesignations,
      ({ roles }) => !_.includes(roles, "admin")
    );
  }

  return _.map(designations, ({ id }) => {
    const designation = _.find(
      DESIGNATIONS_CONSTANTS,
      designation => designation.id == id
    );

    return {
      label: !_.isEmpty(designation) ? t(designation.locale) : "",
      value: id,
      subText: !_.isEmpty(designation) ? t(designation.subTextLocale) : "",
      subtitle: !_.isEmpty(designation) ? t(designation.subTextLocale) : "",
    };
  });
};

export const getUnitPlanFieldValue = ({ fieldKey, unitPlanFields }) => {
  return _.get(_.find(unitPlanFields, { uid: fieldKey }), "value", "");
};

const getUnitPlanFieldResolvedValue = ({ fieldKey, unitPlanFields }) => {
  return _.get(
    _.find(unitPlanFields, { uid: fieldKey }),
    "resolvedMinimalTree",
    ""
  );
};

const getResouceFieldValue = ({ fieldKey, assessmentFields }) => {
  return _.get(_.find(assessmentFields, { uid: fieldKey }), "value", "");
};

const getResouceFieldResolveValue = ({ fieldKey, assessmentFields }) => {
  return _.get(
    _.find(assessmentFields, { uid: fieldKey }),
    "resolvedMinimalTree",
    ""
  );
};

export const getMYPObjectiveRubricOptions = ({
  field_list,
  assessmentFields,
  unitPlanFields,
  measureAssessingValue,
}) => {
  /*
    For MYP_OBJECTIVE_RUBIRC fieldKey is objectiveMYP
    For MYP_INTERDISCIPLINARY_CRITERIA_RUBRIC fieldKey is interdisciplinaryCriteriaMYP
  */
  const fieldKey = _.get(
    _.find(ASSESSMENT_TOOLS, { type: measureAssessingValue }),
    "objectiveFieldKey",
    ""
  );

  const objectiveFieldValue = getResouceFieldValue({
    fieldKey,
    assessmentFields,
  });
  const objectiveFieldResolvedValue = getResouceFieldResolveValue({
    fieldKey,
    assessmentFields,
  });

  const objectiveUnitPlanFieldValue = getUnitPlanFieldValue({
    fieldKey,
    unitPlanFields,
  });
  const objectiveUnitPlanFieldResolvedValue = getUnitPlanFieldResolvedValue({
    fieldKey,
    unitPlanFields,
  });
  const objectiveTemplateFieldObj = field_list[fieldKey];
  return {
    objectiveField: {
      value: objectiveFieldValue,
      unitPlanFieldValue: objectiveUnitPlanFieldValue,
      unitPlanFieldResolvedValue: objectiveUnitPlanFieldResolvedValue,
      resolvedValue: objectiveFieldResolvedValue,

      templateFieldObj: objectiveTemplateFieldObj,
      fieldKey,
    },
  };
};

export const getSubLabel = ({ type, props }) => {
  const { t } = props;
  switch (type) {
    case "todoList": {
      return getCountString({
        count:
          _.get(props, "assignedTodoCount", 0) +
          _.get(props, "overdueTodoCount", 0),
        singularText: t("common:item_pending"),
        pluralText: t("common:item_pending_plural"),
        shouldShowZero: true,
      });
    }

    case "consolidatedTodos": {
      return getCountString({
        count: _.get(props, "consolidatedTodosCount"),
        singularText: t("common:item_pending"),
        pluralText: t("common:item_pending_plural"),
        shouldShowZero: true,
      });
    }

    case "circularList":
      return getCountString({
        count: _.get(props, "unreadCircularCount", 0),
        singularText: t("common:unread"),
        pluralText: t("common:unread"),
        shouldShowZero: true,
      });

    case "familyMessaging":
      return getCountString({
        count: _.get(props, "unreadMessageCount", 0),
        singularText: t("common:unread"),
        pluralText: t("common:unread"),
        shouldShowZero: true,
      });

    case "messaging":
      return getCountString({
        count: _.get(props, "messagingV2UnreadChatCount", 0),
        singularText: t("common:unread"),
        pluralText: t("common:unread"),
        shouldShowZero: true,
      });

    case "reports":
      return getCountString({
        count: _.get(props, "totalReportCount", 0),
        singularText: t("common:report"),
        pluralText: t("common:reports")?.toLowerCase(),
        shouldShowZero: true,
      });

    case "journal":
      return getCountString({
        count: _.get(props, "totalPostCount", 0),
        singularText: t("common:post")?.toLowerCase(),
        pluralText: t("common:post_plural")?.toLowerCase(),
        shouldShowZero: true,
      });
  }
};

// `getRotationDayText` (private) and `getRotationDayTextMemoize` moved to
// the sole consumer's folder:
// src/AppComponents/AttendanceInsightsTable/utils.js

export const checkForSimilarity = ({ attachmentId, metadata }) => {
  return async () => {
    const attachment = getAttachmentFragmentFromCache({
      attachmentId,
      metadata,
    });
    try {
      const response = await client.mutate({
        mutation: checkForSimilarityMutation,
        variables: { input: { attachmentId, metadata } },
        optimisticResponse: {
          __typename: "Mutation",
          integration: {
            __typename: "IntegrationMutation",
            generateSimilarityReport: {
              __typename: "SimilarityReport",
              status: "PROCESSING",
              submissionId: null,
              errorCode: null,
              overallMatchPercentage: null,
            },
          },
        },
        update: (
          cache,
          {
            data: {
              integration: { generateSimilarityReport },
            },
          }
        ) => {
          if (!_.isEmpty(generateSimilarityReport)) {
            const updatedAttachment = update(attachment, {
              $set: {
                ...attachment,
                similarityReport: generateSimilarityReport,
              },
            });

            setTimeout(() => {
              writeAttachmentSimilarityReportInCache({
                attachmentId,
                data: updatedAttachment,
              });
            });
          }
        },
      });
      return response;
    } catch (e) {
      setTimeout(() => {
        writeAttachmentSimilarityReportInCache({
          attachmentId,
          data: attachment,
        });
      });
    }
  };
};

export const cancelCheckSimilarityReport = ({ attachmentId }) => {
  return async () => {
    const attachment = getAttachmentFragmentFromCache({ attachmentId });
    try {
      const response = await client.mutate({
        mutation: cancelCheckSimilarityMutation,
        variables: { input: { attachmentId } },
        optimisticResponse: {
          __typename: "Mutation",
          integration: {
            __typename: "IntegrationMutation",
            cancelSimilarityReport: true,
          },
        },
        update: (
          cache,
          {
            data: {
              integration: { cancelSimilarityReport },
            },
          }
        ) => {
          if (cancelSimilarityReport) {
            const updatedAttachment = update(attachment, {
              $set: {
                ...ATTACHMENT_CREATE_ATTRIBUTES,
                signedUrl: _.get(attachment, "signedUrl", ""),
                ...attachment,
              },
            });

            setTimeout(() => {
              writeAttachmentSimilarityReportInCache({
                attachmentId,
                data: updatedAttachment,
              });
            });
          }
        },
      });
      return response;
    } catch (e) {
      setTimeout(() => {
        writeAttachmentSimilarityReportInCache({
          attachmentId,
          data: attachment,
        });
      });
    }
  };
};

const _getCategoryTabsForNotification = ({
  userType,
  isCurriculumProgramFree,
}) => {
  const categoryTabs = [];
  _.forEach(getNotificationCategory(), tab => {
    const { permissionsToCheck = "all" } = tab;
    const isPermission = tab.perm
      ? ACLStore.can(tab.perm, permissionsToCheck)
      : true;

    let isAllowed = true;
    if (isCurriculumProgramFree) {
      isAllowed = !_.get(tab, "paidOnly", false);
    }

    if (isPermission && isAllowed && _.includes(tab.userTypes, userType)) {
      categoryTabs.push(...tab.filterValue);
    }
  });
  return categoryTabs;
};

export const getCategoryTabsForNotification = _.memoize(
  _getCategoryTabsForNotification,
  JSON.stringify
);

export const replaceEditPreview = string => {
  return _.replace(string, "edit", "preview");
};

export const getLabelsOfSelectedItems = ({
  selectedItems = [],
  itemList = [],
}) => {
  const labelsOfSelectedItems = _.map(selectedItems, value => {
    const currentItemObj = _.find(itemList, item => item.value === value);
    return currentItemObj?.label;
  });
  return labelsOfSelectedItems;
};

/**
 * @param {Array} selectedItems Structure -> [value1,value2 ...]
 * @param {Array} itemList Structure -> [{value:value1,label:label1},{value:value2,label:label2}...]
 * @param {Function} t  Description ->  function returned by i18n
 * @param {String} noneSelectedText Description -> text to display when no item is selected
 * @returns {String}
 */
// Use getLabelValueForMultiFilterLabelButton for FilterLabelButton in the case of checklist dropdown
export const getLabelValueForDropdown = ({
  selectedItems,
  itemList,
  t,
  noneSelectedText,
  showLabelsCount = 1,
  separator = ",",
  showAllText = true,
  moreLabel = "common:more",
}) => {
  const labelsOfSelectedItems = _.map(selectedItems, value => {
    const currentItemObj = _.find(itemList, item => item.value === value);
    return currentItemObj?.label;
  });

  const itemListSize = _.size(itemList);
  const selectedItemsSize = _.size(selectedItems);

  if (_.isEmpty(selectedItems)) {
    return noneSelectedText;
  } else if (selectedItemsSize === 1) {
    return _.first(labelsOfSelectedItems);
  } else if (selectedItemsSize === itemListSize && showAllText) {
    return i18next.t("common:all");
  } else {
    /*
    example ->
    showLabelsCount = 2 and separetor = "and"
    English, Science and 2 more

    showLabelsCount = 1 and separetor = "and"
    English and 3 more
    */
    const visibleLabels = _.slice(labelsOfSelectedItems, 0, showLabelsCount);
    const count = selectedItemsSize - showLabelsCount;
    if (count > 0) {
      return t("common:dropdown_label_with_more_text", {
        labels: visibleLabels.join(", "),
        count,
        separator,
      });
    } else {
      return visibleLabels.join(", ");
    }
  }
};

/**
 * @param {Array} arrayOfObject Structure -> [{id:1,...},{id:2,...},{id:3,...}]
 * @param {Array} idsArray Structure -> [id1, id2]
 * @returns {Array} Structure -> [{id:id1,...},{id:id2,...}]
 */
const getMatchingIdsData = ({ arrayOfObject, idsArray }) => {
  const length = _.size(idsArray);

  let newArray = [];
  for (let i = 0; i < length; i++) {
    const foundObject = _.find(arrayOfObject, { id: idsArray[i] });

    if (!_.isEmpty(foundObject)) newArray = [...newArray, foundObject];
  }

  return newArray;
};

export const getMatchingIdsDataMemoize = _.memoize(
  params => getMatchingIdsData(params),
  params => JSON.stringify(params)
);

/**
 * @param {Array} superSet Structure -> [{id:1,...},{id:2,...},{id:3,...}]
 * @param {Array} subSet Structure -> [{id:1,...},{id:2,...}]
 * @returns {Array} [{id:3,...}]
 */
const getSuperMinusSub = ({ superSet, subSet }) => {
  return _.filter(superSet, ({ id }) => {
    const index = _.findIndex(
      subSet,
      subSetItem => _.get(subSetItem, "id") === id
    );
    const isElementPresent = index >= 0;

    // include if element is not present
    return !isElementPresent;
  });
};

export const getSuperMinusSubMemoize = _.memoize(
  params => getSuperMinusSub(params),
  params => JSON.stringify(params)
);

export const getFamilyInvitePdfType = () => {
  return enableFamilyInviteV2Flow && ACLStore.can(ENABLE_FAMILY_INVITE_FLOW_V2)
    ? "invitecodev3"
    : ACLStore.can(ENABLE_FAMILY_INVITE_PDF_V2)
    ? "invitecodev2"
    : "invitecode";
};

export const convertDataJSONToXLSX = async ({
  jsonSheetData,
  applyCustomStyles,
}) => {
  let ExcelJS;
  try {
    ExcelJS = await import(/* webpackChunkName: "ExcelJS" */ "exceljs");
  } catch (err) {
    window.location.reload();
  }

  // create workbook
  const workbook = new ExcelJS.Workbook();

  jsonSheetData.sheets.forEach(sheet => {
    let sheetName = sheet.name;
    // ExcelJS has limit of 31 characters on worksheet names, more than that need
    // to be trimmed. Otherwise it will automatically trim it, and create sheet,
    // which cannot be accessed later through initial sheet name

    sheetName = _.truncate(sheetName, {
      length: SHEET_NAME_MAX_LENTH,
      omission: "",
    });

    // create a worksheet
    workbook.addWorksheet(sheetName);
    let workSheet = workbook.getWorksheet(sheetName);
    if (_.isEmpty(workSheet))
      throw new Error(`${workSheet} named workSheet not found in workBook`);

    // fill data
    sheet.rows.forEach(section => {
      workSheet.insertRows(section.start, section.dataRows);
    });

    // apply styles
    if (applyCustomStyles) {
      workSheet = applyCustomStyles({ workSheet });
    }
  });
  const xlsxData = await workbook.xlsx.writeBuffer();

  const xlsxDataBlob = new Blob([xlsxData], { type: XLSX_FILE_TYPE });
  return xlsxDataBlob;
};

// This Util downloads XLSX sheet, for provided JSON data

export const updateAIConversation = ({ input }) => {
  return async () => {
    try {
      await client.mutate({
        mutation: updateAIConversationMutation,
        variables: { input },
      });
    } catch (err) {
      console.error(err);
    }
  };
};

const _getToolCalls = ({ parsedResponse }) => {
  const toolCalls = [];
  let currentResponse = {};

  _.forEach(parsedResponse, item => {
    if (_.has(item, "toolCalls")) {
      _.forEach(_.get(item, "toolCalls", []), toolCall => {
        const functionObj = _.find(
          _.get(currentResponse, "tool_calls", []),
          ({ index }) => index === toolCall?.index
        );

        if (!functionObj) {
          currentResponse.tool_calls = _.concat(
            _.get(currentResponse, "tool_calls", []),
            toolCall
          );
        } else {
          if (_.has(toolCall?.function, "name")) {
            functionObj.function.name = toolCall.function.name;
          }
          if (_.has(toolCall?.function, "arguments")) {
            functionObj.function.arguments += toolCall.function.arguments;
          }
        }
      });
    } else {
      if (!_.isEmpty(currentResponse)) {
        toolCalls.push(currentResponse);
        currentResponse = {};
      }
    }
  });

  if (!_.isEmpty(currentResponse)) {
    toolCalls.push(currentResponse);
  }

  return toolCalls;
};

const _getLatestChunkStatus = ({ parsedResponse }) => {
  const lastChunk = _.last(parsedResponse);

  if (_.has(lastChunk, "content")) {
    return "AI_RESPONSE";
  } else if (_.has(lastChunk, "toolCalls") || _.has(lastChunk, "tool")) {
    return "TOOL_INDICATION";
  } else if (_.has(lastChunk, "state")) {
    return "STATE_INDICATION";
  } else if (_.has(lastChunk, "isMetadataChunk")) {
    return "METADATA_CHUNK";
  } else {
    return "UNKNOWN";
  }
};

const _filteredParsedResponse = parsedResponse => {
  // Filter out the objects which are like {keepConnectionAlive: true}
  return _.filter(parsedResponse, item => !_.has(item, "keepConnectionAlive"));
};

const parseStreamResponse = ({ response, useStreamParserV2 = false }) => {
  if (useStreamParserV2) {
    return parseStreamResponseV2({ response });
  }

  let parsedResponse = response;
  if (_.isString(response)) {
    // Wrap the string with square brackets to form a valid JSON array
    const responseArrayString = `[${response.replace(
      /("content"\s*:\s*"[^"]*")|\}\{/g,
      (match, content) => (content ? content : "},{")
    )}]`;
    // Parse the JSON array
    parsedResponse = JSON.parse(responseArrayString);
  } else {
    parsedResponse = [response];
  }

  parsedResponse = _filteredParsedResponse(parsedResponse);
  // Extract the values
  const metadataChunkObj =
    _.find(parsedResponse, item => _.get(item, "isMetadataChunk", false)) || {};

  const content = _.map(
    _.filter(parsedResponse, item => _.has(item, "content")),
    "content"
  ).join("");
  const toolResponse = _.find(parsedResponse, "toolResponse");
  const tools = _.map(
    _.filter(parsedResponse, item => _.has(item, "tool")),
    "tool"
  );
  const state = _.map(
    _.filter(parsedResponse, item => _.has(item, "state")),
    "state"
  );

  const userInputFlagged = _.get(
    _.find(parsedResponse, "userInputFlagged"),
    "userInputFlagged",
    false
  );

  const toolUseResponse = _.map(
    _.filter(parsedResponse, item => _.has(item, "toolUseResponse")),
    "toolUseResponse"
  );

  const toolResponseMessage = _.map(
    _.filter(parsedResponse, item => _.has(item, "toolResponseMessage")),
    "toolResponseMessage"
  );

  const toolCalls = _getToolCalls({ parsedResponse });
  const latestChunkStatus = _getLatestChunkStatus({ parsedResponse });

  return {
    content,
    toolResponse,
    tools,
    state,
    toolCalls,
    latestChunkStatus,
    userInputFlagged,
    toolUseResponse,
    toolResponseMessage,
    ...metadataChunkObj,
  };
};

export const getNotificationCategory = () => {
  return NOTIFICATION_CATEGORY_V2;
};

export const getVariablesForLocalisedPlannerTemplates = ({
  templates,
  language,
}) => {
  const variables = {
    ids: templates,
  };

  if (ACLStore.can(ENABLE_DYNAMIC_LOCALISATION_OF_TEMPLATE)) {
    variables.locale = language;
  }

  return variables;
};

export const getOrganizationGlobalConstantsQueryVariables = orgId => {
  return {
    organizationId: orgId,
    filter: {
      names: ["TIMEZONE"],
    },
  };
};

export const getOrganizationGlobalConstantsQueryVariablesMemoized = _.memoize(
  params => getOrganizationGlobalConstantsQueryVariables(params),
  params => JSON.stringify(params)
);

const getDefaultTTSModel = () => {
  const defaultModel = _.includes(["staging", "development"], getEnvironment())
    ? "toddle-tts-v4" // use v4 model in staging and development
    : "toddle-tts-v2"; // use v2 model for non-staging (preprod, prod)
  return defaultModel;
};

export const parseSchoolCustomInstructions = schoolInstructions => {
  const parsedSchoolCustomInstructions = _.last(
    _.flattenDeep([
      ...schoolInstructions.matchAll(
        /<school_instructions>([\s\S]*?)<\/school_instructions>/g
      ),
    ])
  )
    ?.replace?.(/\\n/g, "\n")
    ?.replace?.(/\\"/g, '"');

  return convertMarkdownToHTML({
    content: parsedSchoolCustomInstructions || "",
  });
};

export const makeStreamRequestViaFunctionUrl = async ({
  url,
  body,
  getCancelToken,
  onDownloadProgress,
  responseParser,
  axiosConfig = {},
}) => {
  const response = await axios.post(url, body, {
    responseType: "stream",
    ...(getCancelToken
      ? { cancelToken: new CancelToken(token => getCancelToken(token)) }
      : {}),
    onDownloadProgress: async ({ event } = {}) => {
      const responseText = _.get(event, "target.response");
      const parsed = responseParser(responseText);
      onDownloadProgress?.(parsed);
    },
    ...axiosConfig, // Callers can override responseType for non-streaming endpoints
  });
  return responseParser(response?.data);
};

export const makeStreamRequestViaWebSocket = ({
  url,
  data,
  action,
  getCancelToken,
  onDownloadProgress,
  responseParser,
}) => {
  return new Promise((resolve, reject) => {
    const socket = new WebSocket(url);
    let responseData = "";
    let isCancelled = false;

    getCancelToken?.(() => {
      isCancelled = true;
      socket.close();
    });

    socket.onopen = () => {
      socket.send(JSON.stringify({ action, data }));
    };

    socket.onmessage = event => {
      responseData += event.data;
      try {
        const parsed = responseParser(responseData);
        onDownloadProgress?.(parsed);
      } catch (e) {
        console.error(e);
      }
    };

    let settled = false;

    socket.onclose = () => {
      if (settled) return;
      settled = true;
      if (isCancelled) {
        const cancelError = new Error("WebSocket request cancelled");
        cancelError.isWebSocketCancel = true;
        reject(cancelError);
        return;
      }
      const finalParsed = responseParser(responseData);
      resolve(finalParsed);
    };

    socket.onerror = () => {
      if (settled) return;
      settled = true;
      reject(new Error("WebSocket connection error"));
    };
  });
};

export const makeStreamRequest = async ({
  isChina,
  functionUrlConfig,
  webSocketConfig,
}) => {
  if (isChina) {
    return makeStreamRequestViaWebSocket(webSocketConfig);
  }
  return makeStreamRequestViaFunctionUrl(functionUrlConfig);
};

export const isWebSocketCancel = error => {
  return error?.isWebSocketCancel === true;
};

const _checkIsChinaSetupEnabled = ({ entityType }) => {
  switch (entityType) {
    case "PROGRESS_REPORT":
      return ACLStore.can("FeatureFlag:EnableProgressReportChinaAISetup");
    default:
      return ACLStore.can("FeatureFlag:EnableChinaAISetup");
  }
};

export const isChinaAIEnhancedSetupEnabled = ({ entityType } = {}) => {
  return (
    getIsChina() &&
    (_checkIsChinaSetupEnabled({ entityType }) ||
      ACLStore.can("FeatureFlag:EnableChinaAISetup"))
  );
};

export const isAudioServicesChinaSetupEnabled = () => {
  return ACLStore.can("FeatureFlag:EnableAudioServicesAIModelChinaSetup");
};

// Auto-generated exports
export {
  PATH_TO_GET_SIGNED_URL,
  CancelToken,
  QUERY_STRING_CONFIG,
  XLSX_FILE_TYPE,
  SHEET_NAME_MAX_LENTH,
  _getIsGlobalSearchJumpToRevampEnabled,
  _getIsGlobalSearchJumpToV2Enabled,
  _shouldTriggerGlobalSearch,
  getExceededSizeToastMsgAndLocaleParams,
  getMutationVariable,
  getIsVirusTaggingEnabled,
  uint8ArrayUtf8ByteString,
  isBufferHeic,
  getUnitPlanFieldResolvedValue,
  getResouceFieldValue,
  getResouceFieldResolveValue,
  _getCategoryTabsForNotification,
  getMatchingIdsData,
  getSuperMinusSub,
  _getToolCalls,
  _getLatestChunkStatus,
  _filteredParsedResponse,
  parseStreamResponse,
  getDefaultTTSModel,
};
